import axios, { type AxiosInstance, type AxiosResponse } from "axios";

/**
 * This is the base class for all API clients. It is used to make requests to
 * the backend. It will include the base URL and the credentials.
 *
 * it's configured to only send application/json content type and to include
 * cookies by default.
 */
export class BaseApiClient {
	private static instance: BaseApiClient;

	// singleton instance
	private client: AxiosInstance;

	private constructor() {
		if (!import.meta.env.VITE_API_URL) {
			throw new Error("VITE_API_URL is not set");
		}
		this.client = axios.create({
			baseURL: `${import.meta.env.VITE_API_URL}/api/v1/`,
			withCredentials: true, // Always send cookies
			headers: {
				"Content-Type": "application/json",
				"Cache-Control": "no-cache, no-store, must-revalidate", // this is used to prevent caching
			},
			timeout: 10000, // 10 seconds
		});

		// Add request interceptor to include JWT token
		this.client.interceptors.request.use((config) => {
			const token = document.cookie
				.split("; ")
				.find((row) => row.startsWith("jwt_token="))
				?.split("=")[1];

			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
			} else {
				console.warn("No JWT token found, request will not be authenticated.");
			}
			return config;
		});

		// Add response interceptor to handle token expiration and warnings
		this.client.interceptors.response.use(
			(response) => {
				// Handle token expiration warning
				if (response.headers["x-token-expiring-soon"] === "true") {
					console.log("Authentication token will expire soon");
					window.dispatchEvent(new CustomEvent("auth:refreshToken"));
				}
				return response;
			},
			async (error) => {
				// Handle expired token errors
				const isTokenExpired =
					error.response?.data?.error === "Token expired" ||
					error.response?.data === '{"error":"Token expired"}';

				if (isTokenExpired) {
					console.log("Authentication token expired, logging out user");
					window.dispatchEvent(new CustomEvent("auth:tokenExpired"));
				}

				return Promise.reject(error);
			},
		);
	}

	public static getInstance(): BaseApiClient {
		if (!BaseApiClient.instance) {
			BaseApiClient.instance = new BaseApiClient();
		}
		return BaseApiClient.instance;
	}

	/**
	 * Template method for GET requests.
	 * @param endpoint
	 * @param params
	 * @returns
	 */
	public async get<T>(
		endpoint: string,
		params: Record<string, unknown> = {},
	): Promise<T> {
		const response: AxiosResponse<T> = await this.client.get(endpoint, {
			params,
		});
		return response.data;
	}

	/**
	 * Template method for POST requests.
	 * @param T The type of the response
	 * @param D The type of the data to send in the request body
	 * @param endpoint
	 * @param data
	 * @returns
	 */
	public async post<T, D>(endpoint: string, data: D): Promise<T> {
		const response: AxiosResponse<T> = await this.client.post(endpoint, data);
		return response.data;
	}

	/**
	 * Template method for POST requests with a custom timeout
	 * @param T The type of the response
	 * @param D The type of the data to send in the request body
	 * @param endpoint
	 * @param data
	 * @param timeout Optional timeout in milliseconds for this specific request
	 * @returns
	 */
	public async postWithTimeout<T, D>(
		endpoint: string,
		data: D,
		timeout?: number,
	): Promise<T> {
		const config = timeout ? { timeout } : {};
		const response: AxiosResponse<T> = await this.client.post(
			endpoint,
			data,
			config,
		);
		return response.data;
	}

	/**
	 * Template method for PATCH requests.
	 * @param endpoint
	 * @param data
	 * @returns
	 */
	public async patch<T, D>(endpoint: string, data: D): Promise<T> {
		const response: AxiosResponse<T> = await this.client.patch(endpoint, data);
		return response.data;
	}

	/**
	 * Template method for PUT requests.
	 * @param endpoint
	 * @param data
	 * @returns
	 */
	public async put<T, D>(endpoint: string, data: D): Promise<T> {
		const response: AxiosResponse<T> = await this.client.put(endpoint, data);
		return response.data;
	}

	/**
	 * Template method for DELETE requests.
	 * @param endpoint
	 * @returns
	 */
	public async delete<T, D>(endpoint: string, data?: D): Promise<T> {
		const response: AxiosResponse<T> = await this.client.delete(endpoint, {
			data,
		});
		return response.data;
	}

	/**
	 * Template method for file uploads.
	 * @param endpoint The API endpoint
	 * @param file The file to upload
	 * @param additionalData Additional form data to include
	 * @returns The response data
	 */
	public async uploadFile<T>(
		endpoint: string,
		file: File,
		additionalData: Record<string, unknown> = {},
	): Promise<T> {
		const formData = new FormData();
		formData.append("file", file);

		// Add any additional data to the form
		for (const [key, value] of Object.entries(additionalData)) {
			formData.append(key, String(value));
		}

		// Create a custom config to override the default Content-Type
		const config = {
			headers: {
				"Content-Type": "multipart/form-data",
			},
			timeout: 60000,
		};

		const response: AxiosResponse<T> = await this.client.post(
			endpoint,
			formData,
			config,
		);
		return response.data;
	}

	/**
	 * Template method for GET requests that return blob data
	 * @param endpoint
	 * @returns Promise with blob data
	 */
	public async getBlob(endpoint: string): Promise<Blob> {
		const response: AxiosResponse<Blob> = await this.client.get(endpoint, {
			responseType: "blob",
		});
		return response.data;
	}
}

export default BaseApiClient;
